/**
 * Contacts + identity types — the SPA half of the lexiun-contacts C1 gateway
 * contract (`ai-docs/lexiun-contacts.md` §2/§6.1/§6.2/§7A; handoff
 * `contacts-identity-ui-lexide-handoff-2026-07-15.md` §3).
 *
 * ⚠ VERBATIM BEHAVIORAL PORT of the runtime C0 Zod schemas
 * (`lexruntime/openclaw/extensions/lexiun-core/src/contacts/contacts-store.ts`
 * + `assistant-identity-store.ts`, shipped `[M:contacts-C0-thin]` 3cf15bc7) —
 * the runtime store is the CANONICAL shape authority; this file is the
 * browser-safe mirror (same pattern as `case-labels.ts` / `session.ts`).
 * KEEP IN SYNC on any schema change.
 *
 * The gateway METHOD names + param/result shapes below are the §3 contract
 * the runtime half exposes under `[M:contacts-C1-api]`; until that marker
 * lands the SPA calls them and degrades gracefully on "method not found".
 */

// ── §4.1 tiers + §4.4 dispositions ──────────────────────────────────────────

export const TRUST_TIERS = ["owner", "trusted", "known", "unknown", "blocked"] as const;
export type TrustTier = (typeof TRUST_TIERS)[number];

export const DISPOSITIONS = [
  "notify-only",
  "acknowledge+relay",
  "routine-per-handlingNotes",
] as const;
export type Disposition = (typeof DISPOSITIONS)[number];

/** §2 seeded relationship vocabulary — OPEN (combobox seeds + free entry;
 *  never a closed enum). */
export const RELATIONSHIP_SEEDS = [
  "client-representative",
  "opposing-party",
  "opposing-counsel",
  "co-counsel",
  "court-staff",
  "perito",
  "witness",
  "government",
  "supplier",
  "lead",
  "personal",
  "other",
  "unknown",
] as const;

/** The owner self-record's well-known id (D-7). */
export const OWNER_CONTACT_ID = "contact-owner";

// ── §2 multi-value groups ────────────────────────────────────────────────────

export interface ContactPhone {
  number: string;
  label?: string;
  primary?: boolean;
  hasWhatsapp?: boolean;
}

export interface ContactEmail {
  address: string;
  label?: string;
  primary?: boolean;
  /** EP2 (§3.9 block ③) — set ONLY by `mail.owner_verify_confirm`, never by
   *  the SPA. Same discipline as `ContactChannelIdentity.verified`
   *  (CT-INV-7): we render it, we never fabricate it. */
  verified?: boolean;
  verifiedAt?: string;
}

/** The peer binding (§4.2). `verified` is set ONLY by the runtime flows
 *  (CT-INV-7) — the SPA renders it, never fabricates it. */
export interface ContactChannelIdentity {
  channel: string;
  peerId: string;
  verified: boolean;
  boundAt: string;
}

export interface ContactSocial {
  network: string;
  handleOrUrl: string;
  label?: string;
}

export interface ContactAddress {
  street?: string;
  number?: string;
  complement?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  label?: string;
  primary?: boolean;
}

export interface CasePartyLink {
  caseId: string;
  partyId: string;
}

// ── §2 the contact record ────────────────────────────────────────────────────

export interface Contact {
  id: string;
  kind: "person" | "organization";
  displayName: string;
  fullLegalName?: string;
  cpf?: string;
  cnpj?: string;
  oabNumber?: string;
  birthDate?: string;
  organizationName?: string;
  jobTitle?: string;
  department?: string;
  phones: ContactPhone[];
  emails: ContactEmail[];
  channels: ContactChannelIdentity[];
  social: ContactSocial[];
  addresses: ContactAddress[];
  relationship: string;
  tags: string[];
  trustTier: TrustTier;
  disposition: Disposition;
  handlingNotes?: string;
  language?: string;
  treatment?: string;
  clientId?: string;
  userId?: string;
  caseParties: CasePartyLink[];
  notes?: string;
  status: "active" | "archived";
  createdAt: string;
  createdBy: "user" | "agent-suggested" | "first-contact-stub";
  updatedAt: string;
}

/**
 * The owner self-record AS THE IDENTITY FORM EDITS IT.
 *
 * Provenance (`createdAt`/`createdBy`/`updatedAt`) is assigned by the runtime
 * on the first `identity.set`, so an owner row that does not exist yet has
 * none — and this type omits them rather than let the form fabricate values
 * for fields it neither renders nor sends. `id` stays because it is the
 * well-known `OWNER_CONTACT_ID`, not something the server mints.
 */
export type OwnerIdentityDraft = Omit<Contact, "createdAt" | "createdBy" | "updatedAt">;

/** A brand-new editable record (upsert create shape — the runtime assigns
 *  id/provenance on create; CT-INV-4 required core defaults applied). */
export type ContactDraft = Omit<Contact, "id" | "createdAt" | "createdBy" | "updatedAt"> & {
  id?: string;
};

// ── §6.2 assistant identity (D-9/D-10) ───────────────────────────────────────

export interface AssistantIdentity {
  schemaVersion: 1;
  name: string;
  theme?: string;
  emoji?: string;
  avatar?: string;
  /** D-10 dedicated, agent-exclusive addresses (plural-capable). */
  emails: ContactEmail[];
  /** Outbound display-name template; `{UserName}` interpolates the owner's
   *  displayName. */
  emailDisplayName?: string;
  updatedAt: string;
}

// ── Setup status (user-setup design §5.1 — the v2 evaluation contract) ──────
// Re-based 2026-07-17 on the USER SETUP subsystem (lexiun-user-setup.md):
// step ids come from the runtime step REGISTRY (im-door / im-owner-pairing /
// whatsapp-door SUPERSEDE the old telegram-bot / telegram-owner / whatsapp
// keys — any-channel semantics live in the step, channel detail in `detail`).

export type SetupStepId =
  | "owner-name"
  | "owner-email"
  | "im-door"
  | "im-owner-pairing"
  /** Endpoint 2 for Telegram — the LAWYER's own account bound as the command
   *  door, via the same 6-digit handshake WhatsApp uses. The two-endpoint rule
   *  is IDENTICAL across both IM channels (§2-bis). */
  | "telegram-owner-pairing"
  | "assistant-mailbox"
  | "assistant-name"
  | "notifications"
  | "whatsapp-door"
  /** Endpoint 2 for WhatsApp — the LAWYER's own number bound as the command
   *  door. `dependsOn: ["whatsapp-door"]`. Without it the assistant answers
   *  that number as a receptionist and NOBODY can command it there, so a
   *  channel with only `whatsapp-door` closed is live but ownerless. */
  | "whatsapp-owner-pairing"
  | "email-rules"
  | "consent-lgpd";

export type SetupTier =
  | "required-blocking"
  | "required-soft"
  | "recommended"
  | "optional"
  | "disabled";

export type SetupStepState = "done" | "pending" | "skipped" | "deferred" | "disabled" | "blocked";

/** A user override recorded via `setup.ack` (skipped/deferred/disabled). */
export type SetupAck = "skipped" | "deferred" | "disabled";

export interface SetupStepItem {
  id: SetupStepId;
  tier: SetupTier;
  done: boolean;
  state: SetupStepState;
  /** Per-step detail ("telegram", "2 endereço(s)", the mailbox address). */
  detail?: string;
  /** Unmet dependencies (state === "blocked"). */
  blockedBy?: SetupStepId[];
  /** The lx_help walkthrough id ("SET-CHANNELS-TELEGRAM"…). */
  guideEntryId?: string;
  /** The in-app deep-link for completing the step. */
  wizardRoute?: string;
  /** Deterministic pt-BR label (same string the concierge digest uses). */
  titlePt: string;
  /** Benefits-led persuasion copy (tone directive 2026-07-17): what the
   *  assistant becomes able to do once the step is done. */
  benefitPt?: string;
}

/** The `setup.status` result — ONE source of truth shared by the concierge's
 *  `lx_setup_status` and the SPA checklist/gate (never recomputed SPA-side).
 *  Carries the D-13 reminder timestamps (persisted runtime-side in
 *  USER_SETUP.json so devices agree). */
export interface SetupStatus {
  /** The minimum operating profile (blocking + soft steps all done). */
  complete: boolean;
  items: SetupStepItem[];
  /** Outstanding required-blocking steps — non-empty ⇒ the SetupGate routes
   *  into the setup surface (DS-2 uniform enforcement). */
  requiredBlockingOutstanding: SetupStepId[];
  /** Ordered pt-BR labels of remindable not-done steps. */
  missing: string[];
  env: "dev" | "prod";
  /** ISO — when the login reminder was last shown (any device). */
  reminderLastShownAt?: string;
  /** ISO — when the user explicitly disabled reminders ("não me lembrar");
   *  auto-resets after 7 days. Absent = not disabled. */
  reminderDisabledAt?: string;
  /**
   * Per-channel TWO-ENDPOINT truth: `botChannels[ch] && ownerImVerified[ch]`.
   *
   * READ THIS — never a bot/door flag alone — to decide whether a channel is
   * usable. Closing only endpoint 1 (the assistant's own door) leaves the
   * channel live and OWNERLESS: it answers strangers as a receptionist and the
   * lawyer cannot command it there. Optional because an older runtime does not
   * send it; treat `undefined` as "unknown", never as `true`.
   */
  channelComplete?: Partial<Record<"telegram" | "whatsapp", boolean>>;
}

// ── §3 gateway method contract (C1) ─────────────────────────────────────────

export interface ContactsListParams {
  /** Free-text query (name/coordinate substring). */
  query?: string;
  relationship?: string;
  tag?: string;
  clientId?: string;
  trustTier?: TrustTier;
  includeArchived?: boolean;
}

export interface ContactsListResult {
  contacts: Contact[];
}

export interface ContactsGetParams {
  contactId: string;
}
export interface ContactsGetResult {
  contact: Contact;
}

export interface ContactsUpsertParams {
  contact: ContactDraft;
}
export interface ContactsUpsertResult {
  contact: Contact;
}

export interface ContactsArchiveParams {
  contactId: string;
  /** false = unarchive (soft-delete only; records never destroyed). */
  archived?: boolean;
}
export interface ContactsArchiveResult {
  contact: Contact;
}

export interface ContactsBindPeerParams {
  contactId: string;
  channel: string;
  peerId: string;
}
export interface ContactsUnbindPeerParams {
  contactId: string;
  channel: string;
  peerId: string;
}

export interface IdentityGetResult {
  /** The §6.1 owner row (a full Contact, id = OWNER_CONTACT_ID) — or `null`
   *  when the user has never completed a first `identity.set`. That is the
   *  runtime's DELIBERATE first-run contract, pinned by its own test
   *  ("identity.get before any set: owner null, assistant D-9 default"): it
   *  refuses to invent a placeholder owner. This field was typed non-null
   *  until 2026-08-02, so no caller wrote the null branch and a fresh user
   *  rendered a permanent "Carregando…" where the name form belongs. */
  owner: Contact | null;
  /** The §6.2 assistant record. */
  assistant: AssistantIdentity;
}

export interface IdentitySetParams {
  /** Partial owner-row edit (record → USER.md digest, never the reverse). */
  owner?: Partial<ContactDraft>;
  /** Partial assistant edit (record → IDENTITY.md + config materialization). */
  assistant?: Partial<Omit<AssistantIdentity, "schemaVersion" | "updatedAt">>;
}

export interface PairOwnerStartParams {
  channel: "telegram" | "whatsapp";
}
export interface PairOwnerStartResult {
  /** The one-time §7A.2 pairing code the lawyer sends from their own app. */
  code: string;
  /** ISO expiry. */
  expiresAt: string;
}
export interface PairOwnerPollResult {
  /**
   * ⚠ THIS MIRROR WAS WRONG ON TWO OF THREE VALUES. Verified on the wire
   * (galvão, 2026-07-24): the runtime emits **`"waiting"`** while pending and
   * **`"paired"`** on success. The original guesses `"pending"` and `"bound"`
   * never arrive; `"expired"` is retained but UNVERIFIED (it needs a lapsed
   * 10-minute TTL to observe).
   *
   * The cost of the `"bound"` guess was concrete: the dialog matched only that
   * value, so a COMPLETED pairing polled forever, showing a live code and
   * "aguardando confirmação…" while the runtime had already bound the owner.
   *
   * A hand-written mirror of a runtime shape with no compiler link — the same
   * drift class as `WhatsAppIdentity.phone` and the missing setup step ids.
   * Consumers MUST treat any unknown value as "keep polling", never as success,
   * so a future rename degrades to a spinner rather than a false "connected".
   */
  state: "waiting" | "paired" | "expired" | "pending" | "bound";
  /** Present on success. */
  peerId?: string;
}

// ── §4A.3-bis email rules (adopted gateway face, 2026-07-16) ────────────────

export interface EmailRule {
  id: string;
  /** Natural-language condition ("quando chegar e-mail do banco X…"). */
  when: string;
  /** Natural-language standing instruction ("…me avise no Telegram"). */
  then: string;
  enabled: boolean;
  /** Audit provenance (who/what created the rule). */
  provenance?: string;
  /** ISO — last time the rule fired (audit trail). */
  lastRunAt?: string;
}

export interface EmailRulesGetResult {
  rules: EmailRule[];
}
export interface EmailRulesSetParams {
  /** Whole-list semantics — the runtime restates each changed rule's effect
   *  in its confirm loop. */
  rules: EmailRule[];
}

/** §3.9 block ③ — EP2 owner-email pairing (mailsystem B2, `bb3ca197c`).
 *
 *  The 6-digit code NEVER crosses the boundary: `start` returns only
 *  `expiresAt` (for the countdown) and the address it mailed. */
export interface MailOwnerVerifyStartParams {
  personalEmail: string;
}
export interface MailOwnerVerifyStartResult {
  expiresAt: string;
  address: string;
}
export interface MailOwnerVerifyConfirmResult {
  verified: boolean;
  address: string;
  verifiedAt: string;
}

/** The five actionable rejections `mail.owner_verify_*` can return. Rendering
 *  each honestly is the point — collapsing them into one "falhou" hides the
 *  only information the user can act on (is the code wrong, stale, or burned?).
 *  `VERIFY_CODE_INCORRECT` carries `attemptsRemaining`;
 *  `VERIFY_RESEND_TOO_SOON` carries `retryAfterSeconds`. */
export type MailVerifyErrorCode =
  | "VERIFY_CODE_INCORRECT"
  | "VERIFY_CODE_EXPIRED"
  | "VERIFY_NO_PENDING"
  | "VERIFY_TOO_MANY_ATTEMPTS"
  | "VERIFY_RESEND_TOO_SOON";

/** Read-only in-flight state for EP2 (mailsystem `48066d777`). `pending` is
 *  null when nothing is awaiting a code. The code itself is never returned,
 *  even to the caller that started the flow. */
export interface MailOwnerVerifyStatusResult {
  /** `attemptsRemaining` rides the SUCCESS payload (mailsystem `9b98cd549`)
   *  because the gateway error envelope forwards only `{code, message}` and
   *  drops extra keys — so a count attached to a failure never arrived as
   *  data. It is also reload-durable: an error is an event, state is not, and
   *  "how many tries do I have left" is exactly the question a user asks after
   *  a refresh. Optional because it needs a roll to appear. */
  pending: { address: string; expiresAt: string; attemptsRemaining?: number } | null;
}
